import {
  LabelPrintRequestStatus,
  type InventoryOrderCarTireQuoteRecord,
  type LabelPrintRequestRecordList
} from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiIcon, type NoddiIconName } from 'noddi-ui-common';
import { ApiErrorMessageInternal, NavioListCard, NoddiBadge, NoddiInfoBanner, type BadgeVariant } from 'noddi-ui-web';
import { pluralizeWithValue } from 'noddi-util';
import { InventoryOrderSummary } from './InventoryOrderSummary';

/** A received order as it was when saved; the expected list no longer holds it. */
export interface ReceivedInventoryOrder {
  order: InventoryOrderCarTireQuoteRecord;
  storageUnitLabel: string;
  storageObjectGroupId: number | null;
}

type Props = {
  receivedOrders: ReceivedInventoryOrder[];
  /** The labels the receive put on the print list. */
  printRequests: LabelPrintRequestRecordList[];
};

// The print list can be printed from any computer at the facility; checking this often keeps the status current.
const PRINT_STATUS_POLL_MS = 5000;

const PRINT_STATUS_BADGE: Record<
  LabelPrintRequestStatus,
  { variant: BadgeVariant; icon: NoddiIconName; label: string }
> = {
  [LabelPrintRequestStatus.PENDING]: { variant: 'warning', icon: 'Clock', label: 'Waiting to print' },
  [LabelPrintRequestStatus.PRINTED]: { variant: 'success', icon: 'CircleCheck', label: 'Printed' },
  [LabelPrintRequestStatus.CANCELLED]: { variant: 'destructive', icon: 'CircleX', label: 'Canceled' }
};

const nextSteps = (labelCount: number): { icon: NoddiIconName; title: string; description: string }[] => [
  { icon: 'List', title: 'Open the print list', description: 'Use the button below' },
  {
    icon: 'Printer',
    title: 'Print the labels',
    description: `${pluralizeWithValue({ value: labelCount, word: 'label' })}, one per tire`
  },
  { icon: 'Tag', title: 'Attach the labels to the tires', description: 'Then put each set in its storage unit' }
];

/** The labels the receive put on the print list, followed until they are printed. */
export const ReceivedLabelsStep = ({ receivedOrders, printRequests: sentPrintRequests }: Props) => {
  const printRequestIds = sentPrintRequests.map((request) => request.id);
  const { data: polledPrintRequests, error: printRequestsError } = noddiAsync.useGet({
    type: URLKeys.getPaginatedLabelPrintRequests,
    input: { ids: printRequestIds },
    queryConfig: {
      enabled: printRequestIds.length > 0,
      refetchInterval: (query) =>
        query.state.data?.results.some((request) => request.status === LabelPrintRequestStatus.PENDING) === false
          ? false
          : PRINT_STATUS_POLL_MS
    }
  });
  const printRequests = polledPrintRequests?.results ?? sentPrintRequests;
  const statusByGroupId = new Map(printRequests.map((request) => [request.storageObjectGroupId, request.status]));
  const isAllPrinted =
    printRequests.length > 0 && printRequests.every((request) => request.status === LabelPrintRequestStatus.PRINTED);
  const labelCount = receivedOrders.reduce((sum, { order }) => sum + order.numTires, 0);

  return (
    <>
      <NoddiInfoBanner
        variant={isAllPrinted ? 'success' : 'info'}
        icon='Printer'
        title={isAllPrinted ? 'Labels printed' : 'Labels on the print list'}
        texts={[
          {
            value: isAllPrinted ? 'Pick them up at the label printer' : 'This updates by itself once they are printed.'
          }
        ]}
      />
      {printRequestsError && <ApiErrorMessageInternal error={printRequestsError} />}
      <NavioListCard
        title='What to do now'
        size='small'
        items={nextSteps(labelCount).map((step, index) => ({ ...step, number: index + 1 }))}
        getItemKey={({ title }) => title}
        // The steps are numbered, so a count beside the title only repeats the last number.
        hideCount
        renderItem={({ number, icon, title, description }) => (
          <>
            <span className='w-4 shrink-0 font-medium text-muted-foreground tabular-nums'>{number}</span>
            <div className='flex min-w-0 flex-1 flex-col'>
              <span className='font-medium'>{title}</span>
              <span className='text-muted-foreground'>{description}</span>
            </div>
            <NoddiIcon name={icon} size='small' className='shrink-0 text-muted-foreground' />
          </>
        )}
      />
      <NavioListCard
        title='Labels'
        size='small'
        items={receivedOrders}
        getItemKey={({ order }) => order.id}
        renderItem={({ order, storageObjectGroupId }) => {
          const status = storageObjectGroupId == null ? undefined : statusByGroupId.get(storageObjectGroupId);
          const badge = status ? PRINT_STATUS_BADGE[status] : undefined;
          return (
            <>
              <div className='min-w-0 flex-1'>
                <InventoryOrderSummary order={order} />
              </div>
              {badge && (
                <NoddiBadge variant={badge.variant}>
                  <NoddiIcon name={badge.icon} size='small' />
                  {badge.label}
                </NoddiBadge>
              )}
            </>
          );
        }}
      />
    </>
  );
};
